import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, formatError } from "@/lib/api";
import { FilePlus, Trash, Spinner, FilePdf, FileDoc, FileXls, FileImage, File as FileIcon } from "@phosphor-icons/react";

/**
 * DocumentUploader — inline document list with add + remove.
 *
 * Props:
 *  value       Array<{name, url, type, size, uploaded_at}>  — current documents list
 *  onChange(next)                                           — updated list
 *  category    string  — storage category ("profile" | "employee" | ...)
 *  testId      string
 *  disabled    bool
 */
export default function DocumentUploader({ value = [], onChange, category = "profile", testId = "docs", disabled = false }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  function fullSrc(url) {
    if (!url) return "#";
    if (url.startsWith("http")) return url;
    const base = API_BASE.replace(/\/api$/, "");
    return `${base}${url}`;
  }

  function iconFor(type) {
    if (!type) return FileIcon;
    if (type.startsWith("image/")) return FileImage;
    if (type === "application/pdf") return FilePdf;
    if (type.includes("word")) return FileDoc;
    if (type.includes("excel") || type.includes("spreadsheet")) return FileXls;
    return FileIcon;
  }

  async function onFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File exceeds 15 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/upload/document?category=${encodeURIComponent(category)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const doc = {
        name: data.original_filename || f.name,
        url: data.url,
        type: data.content_type,
        size: data.size,
        uploaded_at: new Date().toISOString(),
      };
      onChange?.([...(value || []), doc]);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  }

  function removeAt(i) {
    const next = [...(value || [])];
    next.splice(i, 1);
    onChange?.(next);
  }

  return (
    <div className="space-y-2" data-testid={`${testId}-list`}>
      {(value || []).length === 0 ? (
        <div className="text-sm text-[#a1a1aa]">No documents uploaded yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {value.map((d, i) => {
            const Icon = iconFor(d.type);
            return (
              <li
                key={i}
                data-testid={`${testId}-item-${i}`}
                className="flex items-center gap-3 border border-[#e4e4e7] px-3 py-2 bg-white"
              >
                <Icon size={20} weight="regular" className="text-[#52525b] flex-shrink-0" />
                <a
                  href={fullSrc(d.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 text-sm font-medium text-[#09090b] hover:text-[#002FA7] truncate"
                  data-testid={`${testId}-link-${i}`}
                >
                  {d.name}
                </a>
                <span className="mono text-[10px] text-[#a1a1aa]">
                  {d.size ? `${Math.round(d.size / 1024)} KB` : ""}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    data-testid={`${testId}-remove-${i}`}
                    className="text-[#a1a1aa] hover:text-[#FF2A2A] p-1"
                    aria-label="Remove document"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!disabled && (
        <>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid={`${testId}-add-btn`}
            className="inline-flex items-center gap-1.5 border border-dashed border-[#e4e4e7] px-3 py-2 text-xs font-semibold text-[#52525b] hover:bg-[#f4f4f5] disabled:opacity-50"
          >
            {busy ? <Spinner size={14} className="animate-spin" /> : <FilePlus size={14} weight="bold" />}
            {busy ? "Uploading…" : "Add document"}
          </button>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={onFile}
            data-testid={`${testId}-input`}
          />
          <div className="text-[10px] text-[#a1a1aa]">PDF · Word · Excel · Image · TXT · max 15 MB</div>
        </>
      )}
    </div>
  );
}
